import React, { createRef, useContext, useEffect, useState } from 'react'
import { BlocLiensModel } from '../types/LandingPageSEOModel'
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { FormattedMessage } from 'react-intl'
import Link from '../../../components/link/Link'
import { WHITE } from '../../../theme'
import styles from '../../../styles'
import parse from 'html-react-parser'

import Button from '../../../components/button/Button'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import { isEmpty } from 'lodash'

const useStyles = makeStyles<{ isLargeScreen: boolean, afficherPlus: boolean, possedeLiens: boolean, possedeTexte: boolean }>()(
	(theme, { isLargeScreen, afficherPlus, possedeLiens, possedeTexte }) => ({
		blocLiensContainer: {
			background: WHITE,
			borderRadius: 30,
			padding: isLargeScreen ? `25px 0 ${possedeTexte ? 45 : 0}px` : `43px 0 ${possedeTexte ? 25 : 0}px`,
			position: 'relative',
			marginBottom: isLargeScreen ? 116 : 99
		},
		liensContainer: {
			paddingBottom: 23,
			overflowX: 'auto',
			'& .flexItem': {
				flexShrink: isLargeScreen ? 0 : 'unset',
				marginRight: isLargeScreen ? 0 : 30,
				minWidth: isLargeScreen ? 'unset' : 'fit-content'
			},
			'&::-webkit-scrollbar': {
				width: 16
			},
			'&::-webkit-scrollbar-thumb': {
				background: 'transparent',
				borderRadius: 30,
				boxShadow: isLargeScreen ? 'inset 0 0 10px 10px #D0D5DA' : 'none',
				border: 'solid 4px transparent'
			},
			'& > div:first-child': {
				marginLeft: isLargeScreen ? 35 : 32
			}
		},
		decouvrezAussiContainerSmallScreen: {
			position: 'absolute',
			top: -14,
			left: 0,
			zIndex: 1
		},
		decouvrezAussiStyle: {
			background: theme.palette.accessibilityGreenToWhite.main,
			borderRadius: 21,
			padding: '0 11px 0 13px',
			...styles(theme).text6,
			fontWeight: 600,
			textTransform: 'uppercase',
			color: theme.palette.accessibilityWhiteToBlue.main,
			height: 28,
			display: 'flex',
			alignItems: 'center',
			lineHeight: '21px'
		},
		styleLien: {
			fontWeight: 600,
			minWidth: isLargeScreen ? 250 : 130,
			width: 'max-content',
			marginRight: 60,
			'&:hover': {
				textDecoration: 'underline'
			}
		},
		flouLiens: {
			position: 'absolute',
			top: 0,
			width: 40,
			height: '100%'
		},
		flouGaucheLiens: {
			left: -1,
			background: 'linear-gradient(270deg, rgba(255,255,255,0), rgb(255,255,255))',
			borderRadius: '30px 0 0 30px'
		},
		flouDroitLiens: {
			right: -1,
			background: 'linear-gradient(90deg, rgba(255,255,255,0), rgb(255,255,255))',
			borderRadius: '0 30px 30px 0'
		},
		texteContainer: {
			// Si on n'a pas cliqué sur le bouton "Afficher plus", alors on limite la hauteur à 100px
			maxHeight: afficherPlus ? 'unset' : isLargeScreen ? 100 : 183,
			overflowY: afficherPlus ? 'unset' : 'hidden',
			position: 'relative',
			borderTop: possedeLiens ? `1px solid ${theme.palette.blue['20']}` : 'none',
			paddingTop: possedeLiens ? 31 : 0,
			marginRight: isLargeScreen ? 35 : 32,
			marginLeft: isLargeScreen ? 35 : 32,
			'& h2, & p': {
				margin: 0
			},
			'& h2': {
				...styles(theme).text29,
				fontSize: 18,
				lineHeight: '31px'
			},
			'& p': {
				...styles(theme).text2,
				lineHeight: '20px'
			}
		},
		boutonVoirPlus: {
			position: 'absolute',
			bottom: -25,
			left: '50%',
			transform: 'translate(-50%, 0)',
			border: '1px solid #12294134',
			borderRadius: 25,
			width: 121,
			height: 50
		},
		flouTexte: {
			position: 'absolute',
			bottom: 0,
			left: 0,
			width: '100%',
			height: 60,
			background: 'linear-gradient(180deg, rgba(255,255,255,0), rgb(255,255,255))'
		}
	})
)

interface AcceptingProps {
	blocLiens: BlocLiensModel
}

type BlocLiensProps = AcceptingProps

const BlocLiens: React.FC<BlocLiensProps> = (
	{
		blocLiens
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	// On affiche directement tout le texte s'il n'y a pas de JS
	const [afficherPlus, setAfficherPlus] = useState(typeof window === 'undefined')
	const { classes, cx } = useStyles({ isLargeScreen, afficherPlus, possedeLiens: !isEmpty(blocLiens.liens), possedeTexte: (blocLiens.texte && blocLiens.texte.length > 0) as boolean })
	const texteRef = createRef<HTMLDivElement>()
	const HAUTEUR_VOIR_PLUS_BOUTON = 94

	useEffect(
		() => {
			if (texteRef.current && !afficherPlus) {
				setAfficherPlus(texteRef.current.clientHeight < HAUTEUR_VOIR_PLUS_BOUTON)
			}
		}, [texteRef, afficherPlus]
	)

	return (
		<Grid container item xs={12} className={classes.blocLiensContainer}>
			{
				// Affichage du bloc "Découvrez aussi" en mobile en dehors du Grid pour le positionner tout en haut
				!isLargeScreen && !isEmpty(blocLiens.liens) && (
					<Grid
						container
						item
						xs={10}
						alignItems="center"
						className={classes.decouvrezAussiContainerSmallScreen}
					>
							<span className={classes.decouvrezAussiStyle}>
								<FormattedMessage
									id="blocLiens.decouvrezAussi"
									defaultMessage="Découvrez aussi"
									description="Texte pour découvrir d'autres landing page"
								/>
							</span>
					</Grid>
				)
			}

			{
				// Affichage des liens si présent dans le CMS
				!isEmpty(blocLiens.liens) && (
					<Grid
						container
						item
						xs={12}
						className={classes.liensContainer}
						wrap="nowrap"
					>
						{
							// Affichage du bloc dans le Grid contenant la liste des liens uniquement en grand écran
							isLargeScreen && (
								<Grid
									container
									item
									md={3}
									alignItems="center"
									className="flexItem"
								>
									<span className={classes.decouvrezAussiStyle}>
										<FormattedMessage
											id="blocLiens.decouvrezAussi"
											defaultMessage="Découvrez aussi"
											description="Texte pour découvrir d'autres landing page"
										/>
									</span>
								</Grid>
							)
						}

						{
							blocLiens.liens.map(
								(lien) => (
									<Grid item xs key={lien.titre} className="flexItem">
										<Link
											// On rajoute un "/" car l'URL dans le CMS est au format "https://residence.youfirst.co/fr/landing/[URL saisie dans le champ]" (© spec CMS)
											href={`/${lien.aliasURL}`}
											color="colorAccessibility"
											textStyle="text17"
											className={classes.styleLien}
											noAnimation
										>
											{lien.titre}
										</Link>
									</Grid>
								)
							)
						}

						<div className={cx(classes.flouLiens, classes.flouGaucheLiens)} />
						<div className={cx(classes.flouLiens, classes.flouDroitLiens)} />
					</Grid>
				)
			}

			{
				// Affichage du texte si présent dans le CMS
				blocLiens.texte && (
					<>
						<Grid item xs={12} className={classes.texteContainer} ref={texteRef}>
							{parse(blocLiens.texte)}
							{
								!afficherPlus && (
									<div className={classes.flouTexte} />
								)
							}
						</Grid>

						{
							!afficherPlus && (
								<Button
									background="backgroundWhite"
									color="colorAccessibility"
									textStyle="text4"
									onClick={() => setAfficherPlus(true)}
									buttonStyle={classes.boutonVoirPlus}
								>
									<FormattedMessage
										id="blocLiens.voirPlus"
										defaultMessage="Voir plus +"
										description="Bouton pour voir plus de texte"
									/>
								</Button>
							)
						}
					</>
				)
			}
		</Grid>
	)
}

export default BlocLiens